// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { HorizontalRule } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Roboto } from "@mcmc/XDL/Utilities/sharedStyles";
import mergeToNew from "@mcmc/XDL/Utilities/xMergeToNew";
import React, { FC } from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    orderManagmentSectionTitle = "orderManagmentSectionTitle",
    orderManagmentLinks = "orderManagmentLinks",
    accountManagmentTitle = "accountManagmentTitle",
    accountManagmentLinks = "accountManagmentLinks",
    extendedStyles = "extendedStyles",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.orderManagmentSectionTitle]: string;
        [fields.orderManagmentLinks]: LinkModel[];
        [fields.accountManagmentTitle]: string;
        [fields.accountManagmentLinks]: LinkModel[];
        [fields.extendedStyles]?: MyAccountQuickLinksStyles;
    };
}

const mapLinks = (state: ApplicationState, widgetLinks: LinkModel[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    return widgetLinks
        .map(widgetLink => {
            const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
            if (pageLink) {
                return { ...pageLink, title: widgetLink.fields.overriddenTitle || pageLink?.title };
            }
            return undefined;
        })
        .filter(pageLink => (pageLink?.parentId === myAccountPage?.id ? !pageLink?.excludeFromNavigation : true));
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const orderManagmentLinks = mapLinks(state, ownProps.fields.orderManagmentLinks);
    const accountManagmentLinks = mapLinks(state, ownProps.fields.accountManagmentLinks);
    const linkGroups = [
        { title: ownProps.fields.orderManagmentSectionTitle, links: orderManagmentLinks },
        { title: ownProps.fields.accountManagmentTitle, links: accountManagmentLinks },
    ];
    const myLocalBranch: WarehouseModel = JSON.parse(getSession(state).properties.defaultWarehouse || "null");
    return {
        linkGroups,
        myLocalBranch,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface MyAccountQuickLinksStyles {
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    contactLink?: LinkPresentationProps;
    addressWrapper?: InjectableCss;
    myBranchLinkWrapper?: InjectableCss;
}

export const myAccountQuickLinksStyles: MyAccountQuickLinksStyles = {
    linkListWrapper: {
        css: css`
            margin-bottom: 20px;
        `,
    },
    titleWrapper: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            margin-bottom: 20px;
            padding: 5px 0;
        `,
    },
    title: {
        variant: "h6",
        lineHeight: "19px",
        css: css`
            margin: 0;
        `,
    },
    linkWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    myBranchLinkWrapper: {
        css: css`
            margin-bottom: 5px;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    link: {
        color: "secondary",
        typographyProps: {
            weight: 400,
            ...Roboto,
        },
        css: css`
            ${getProp("theme.link.defaultProps.css")}
            font-size: 14px;
            line-height: 16px;
        `,
    },
    addressWrapper: {
        css: css`
            margin-bottom: 15px;
        `,
    },
};

const LinkLabel = styled.span`
    font-weight: 700;
    text-transform: capitalize;
    margin-right: 5px;
`;

const MyAccountQuickLinks: FC<Props> = ({ linkGroups, myLocalBranch, fields: { extendedStyles } }) => {
    const [styles] = React.useState(() => mergeToNew(myAccountQuickLinksStyles, extendedStyles));

    const linksSection = linkGroups.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkListWrapper} key={index}>
                    {linkGroup.title && (
                        <StyledWrapper {...styles.titleWrapper}>
                            <Typography {...styles.title}>{linkGroup.title}</Typography>
                        </StyledWrapper>
                    )}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkWrapper}>
                                    <Link href={link?.url} {...styles.link}>
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });
    return (
        <>
            {linksSection}
            {myLocalBranch && (
                <StyledWrapper {...styles.linkListWrapper}>
                    <StyledWrapper {...styles.titleWrapper}>
                        <Typography {...styles.title}>{translate("My Local Branch")}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.addressWrapper}>
                        <AddressInfoDisplay
                            companyName={myLocalBranch.description || myLocalBranch.name}
                            address1={myLocalBranch.address1}
                            address2={myLocalBranch.address2}
                            state={myLocalBranch.state}
                            postalCode={myLocalBranch.postalCode}
                            city={myLocalBranch.city}
                            extendedStyles={{
                                companyNameText: {
                                    css: css`
                                        margin-bottom: 5px;
                                    `,
                                },
                            }}
                        />
                    </StyledWrapper>
                    {myLocalBranch.phone && (
                        <StyledWrapper {...styles.myBranchLinkWrapper}>
                            <LinkLabel>{translate("Tel:")}</LinkLabel>
                            <Link
                                href={`tel:${myLocalBranch.phone}`}
                                onClick={(e: any) => {
                                    e.defaultPrevented = true;
                                }}
                                {...styles.link}
                                aria-label={translate("Tel: {0}", myLocalBranch.phone)}
                            >
                                {myLocalBranch.phone}
                            </Link>
                        </StyledWrapper>
                    )}
                    {myLocalBranch.properties.fax && (
                        <StyledWrapper {...styles.myBranchLinkWrapper}>
                            <LinkLabel>{translate("Fax:")}</LinkLabel>
                            <Link
                                href={`tel:${myLocalBranch.properties.fax}`}
                                onClick={(e: any) => {
                                    e.defaultPrevented = true;
                                }}
                                {...styles.link}
                                aria-label={translate("Fax: {0}", myLocalBranch.properties.fax)}
                            >
                                {myLocalBranch.properties.fax}
                            </Link>
                        </StyledWrapper>
                    )}
                    {myLocalBranch.properties.email && (
                        <StyledWrapper {...styles.myBranchLinkWrapper}>
                            <LinkLabel>{translate("Mail:")}</LinkLabel>
                            <Link
                                href={`mailto:${myLocalBranch.properties.email}`}
                                onClick={(e: any) => {
                                    e.defaultPrevented = true;
                                }}
                                {...styles.link}
                                aria-label={translate("Email: {0}", myLocalBranch.properties.email)}
                            >
                                {myLocalBranch.properties.email}
                            </Link>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

const myAccountQuickLinks: WidgetModule = {
    component: connect(mapStateToProps)(MyAccountQuickLinks),
    definition: {
        group: "My Account",
        fieldDefinitions: [
            {
                name: fields.orderManagmentSectionTitle,
                fieldType: "Contextual",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Product & Order Management",
            },
            {
                displayName: "Links",
                name: fields.orderManagmentLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                        allowUrls: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: "horizontalRule",
                editorTemplate: "HorizontalRule",
                fieldType: "General",
                defaultValue: "",
            },
            {
                name: fields.accountManagmentTitle,
                fieldType: "Contextual",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Account Management",
            },
            {
                displayName: "Links",
                name: fields.accountManagmentLinks,
                editorTemplate: "ListField",
                fieldType: "Contextual",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const overriddenTitle = item.fields.overriddenTitle;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                },
                defaultValue: [],
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                        allowCategories: item => false,
                        allowUrls: item => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
        ],
    },
};

export default myAccountQuickLinks;
